import { nullableArray, nullableOptional } from '@shigo-ui/api-client';
import { z } from 'zod';

import { NetworkAvailableFeature } from '../../auth/networkFeatures';
import { isAssignableShigoRole, ShigoRole, type AssignableShigoRole } from '../../auth/permissions';

export enum NetworkStatus {
  Active = 'ACTIVE',
  Archived = 'ARCHIVED',
}

export enum NetworkSortProperty {
  Name = 'name',
  CreatedBy = 'createdBy',
  CreatedOn = 'createdOn',
  UpdatedOn = 'updatedOn',
}

export enum NetworkSortDirection {
  Asc = 'ASC',
  Desc = 'DESC',
}

export enum NetworkRegion {
  Us = 'use',
  Eu = 'euc',
}

export enum NetworkTcfEnforcement {
  Off = 'OFF',
  On = 'ON',
  DpOnly = 'DP_ONLY',
}

export enum NetworkCostType {
  Cpm = 'CPM',
  RevenueShare = 'REVSHARE',
}

export enum NetworkCpmMetric {
  AdRequests = 'AD_REQUESTS',
  FilledImpressions = 'FILLED_IMPRESSIONS',
  AdImpressions = 'AD_IMPRESSIONS',
}

export enum NetworkToggleState {
  On = 'ON',
  Off = 'OFF',
}

const createdBySchema = z.union([
  z.string(),
  z.looseObject({ name: nullableOptional(z.string()), email: nullableOptional(z.string()) }),
]);

export const networkCostSchema = z
  .looseObject({
    costType: z.enum(NetworkCostType),
    cpmMetric: nullableOptional(z.enum(NetworkCpmMetric)),
    value: z.number().nullable(),
  })
  .nullable();

export const networkSettingsSchema = z.looseObject({
  id: nullableOptional(z.union([z.string(), z.number()]).transform(String)),
  region: z.enum(NetworkRegion),
  platformName: z.string(),
  rootDomain: z.string(),
  exchangeBidderName: z.string(),
  backendDomain: nullableOptional(z.string()),
  exchangeDomain: nullableOptional(z.string()),
  trackersDomain: nullableOptional(z.string()),
  cdn: nullableOptional(z.string()),
  a2v: nullableOptional(z.string()),
  tagId: nullableOptional(z.number()),
  logoUrl: nullableOptional(z.string()),
  logoLightUrl: nullableOptional(z.string()),
  faviconUrl: nullableOptional(z.string()),
  supportEmail: nullableOptional(z.string()),
  s3bucketDo: nullableOptional(z.string()),
  cdnDo: nullableOptional(z.string()),
  rosConfig: nullableOptional(z.string()),
});

export const networkBrandDetailsSchema = z.looseObject({
  logoUrl: z.string().trim().min(1).nullable(),
  logoLightUrl: z.string().trim().min(1).nullable(),
  accent: z.string(),
  accentStrong: z.string(),
  sidebar: nullableOptional(z.string()),
});

export const networkSchema = z.looseObject({
  id: z.union([z.string(), z.number()]).transform(String),
  name: z.string(),
  status: z.enum(NetworkStatus),
  qps: nullableOptional(z.number()),
  saasCost: networkCostSchema.optional().default(null),
  adstxtDomain: nullableOptional(z.string()),
  dashboardDomain: nullableOptional(z.string()),
  tcfEnforcementEnabled: nullableOptional(z.enum(NetworkTcfEnforcement)),
  skipDefaultNetworkNode: z.boolean().optional().default(false),
  features: nullableArray(z.enum(NetworkAvailableFeature)),
  allowedRoles: nullableArray(z.enum(ShigoRole)).transform((roles) =>
    roles.filter(isAssignableShigoRole),
  ),
  brandDetails: networkBrandDetailsSchema.nullable().optional().default(null),
  networkSettings: nullableArray(networkSettingsSchema),
  createdBy: nullableOptional(createdBySchema),
  createdOn: nullableOptional(z.string()),
  updatedOn: nullableOptional(z.string()),
});

export const networksPageSchema = z.looseObject({
  content: z.array(networkSchema),
  totalElements: z.number().int().nonnegative(),
});

export const networkMutationResponseSchema = z.preprocess(
  (value) => (value === '' || value === undefined ? null : value),
  networkSchema.nullable(),
);

export type Network = z.infer<typeof networkSchema>;
export type NetworkBrandDetails = z.infer<typeof networkBrandDetailsSchema>;
export type NetworkSettings = z.infer<typeof networkSettingsSchema>;
export type NetworksPage = z.infer<typeof networksPageSchema>;

export interface NetworksFilter {
  searchTerm: string;
  statuses: NetworkStatus[];
  page: number;
  size: number;
  sortProperty: NetworkSortProperty | null;
  sortDirection: NetworkSortDirection;
}

export interface NetworkSettingsPayload {
  id?: string | undefined;
  region: NetworkRegion;
  platformName: string;
  rootDomain: string;
  exchangeBidderName: string;
  backendDomain: string;
  exchangeDomain: string;
  trackersDomain: string;
  cdn: string;
  a2v: string;
  tagId: number | null;
  logoUrl: string;
  logoLightUrl: string;
  faviconUrl: string;
  supportEmail: string;
  s3bucketDo: string;
  cdnDo: string;
  rosConfig: string;
}

export interface NetworkMutationPayload {
  name: string;
  qps: number | null;
  status?: NetworkStatus | undefined;
  saasCost: {
    costType: NetworkCostType;
    cpmMetric: NetworkCpmMetric | null;
    value: number;
  } | null;
  adstxtDomain: string;
  dashboardDomain: string;
  tcfEnforcementEnabled: NetworkTcfEnforcement;
  skipDefaultNetworkNode: boolean;
  features: NetworkAvailableFeature[];
  allowedRoles: AssignableShigoRole[];
  brandDetails?: {
    accent: string;
    accentStrong: string;
    sidebar: string | null;
  };
  networkSettings?: NetworkSettingsPayload[] | undefined;
  networkSettingsUpdateRequests?: NetworkSettingsPayload[] | undefined;
}

export interface NetworkBrandAssetFiles {
  logo: File | null;
  logoLight: File | null;
}

export const networkPageSizes = [5, 10, 25, 50, 100] as const;
export const defaultNetworksFilter: NetworksFilter = {
  searchTerm: '',
  statuses: [NetworkStatus.Active],
  page: 0,
  size: 10,
  sortProperty: NetworkSortProperty.UpdatedOn,
  sortDirection: NetworkSortDirection.Desc,
};

export function isNetworkSortProperty(value: string): value is NetworkSortProperty {
  return Object.values(NetworkSortProperty).includes(value as NetworkSortProperty);
}

export function resolveNetworkCreatedBy(network: Network) {
  if (typeof network.createdBy === 'string') return network.createdBy;
  return network.createdBy?.name ?? network.createdBy?.email ?? '-';
}
